import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaEnvelope, FaPhone, FaMapMarkerAlt } from 'react-icons/fa';
// Footer logo uses pre-resized variants (cwebp -resize) imported via
// webpack so the bundled URLs get content-hashed names (e.g.
// /static/media/logo-256.4f3c2a.webp). Originally these were unhashed
// at /images/brand/, which got cache-poisoned by the SPA catch-all
// in _redirects (April 2026 incident). Hashed paths are immune —
// each build produces fresh URLs that can't have been cached as
// anything else. The 256 / 440 variants below are the smallest sizes
// that cover the displayed 220×220 desktop / 160×160 mobile dimensions
// at 2× DPR (saves ~91 KiB vs. the 500×500 master).
import bcLogo   from '../assets/images/british-council.png';
import ucasLogo from '../assets/images/ucas.png';
// ICEF logo removed 2026-05-19 (see Hero.js for same removal rationale —
// native multi-colour SVG vs. brand-tint filter incompatibility).
import logo256  from '../assets/brand/logo-256.webp';
import logo440  from '../assets/brand/logo-440.webp';
// Pearson partner logo — same two-variant srcset as Hero.js. Footer
// displays at 26 px height mobile / 40 px desktop (88×40 displayed
// dimensions at the widest), so the 150-wide variant covers mobile
// at up to 2× DPR and the 400-wide variant covers desktop at 3× DPR.
import pearson150 from '../assets/brand/pearson-150.webp';
import pearson400 from '../assets/brand/pearson-400.webp';
import { trackEvent } from '../lib/analytics';
import { EMAIL, PHONE_TEL, PHONE_DISPLAY } from '../lib/contact';
import './Footer.css';

// Login destinations live in the CRM Next.js app, but until the CRM auth
// flow is live we route to the in-website Coming Soon page. Gating mirrors
// Navbar.js — see that file for the full rationale.
const CRM_URL = process.env.REACT_APP_CRM_URL || '';
const PORTALS_LIVE = process.env.REACT_APP_PORTALS_LIVE === 'true' && !!CRM_URL;
const STUDENT_LOGIN_HREF = PORTALS_LIVE ? `${CRM_URL}/student-login` : '/student-portal';
const STAFF_LOGIN_HREF   = PORTALS_LIVE ? `${CRM_URL}/login`         : '/agent-portal';

export default function Footer() {
  const { t } = useTranslation();

  return (
    <footer className="site-footer">

      {/* ── Brand + links + partners ── */}
      <div className="footer-main">
        <div className="footer-brand">
          <img
            src={logo440}
            srcSet={`${logo256} 256w, ${logo440} 440w`}
            sizes="(max-width: 768px) 160px, 220px"
            alt="Academy Education Network"
            className="footer-logo-img"
            width="440"
            height="440"
            loading="lazy"
            decoding="async"
          />
          <div className="footer-contact">
            <a href={`mailto:${EMAIL}`}>
              <FaEnvelope /> {EMAIL}
            </a>
            <a href={`tel:${PHONE_TEL}`}>
              <FaPhone /> {PHONE_DISPLAY}
            </a>
            <span>
              <FaMapMarkerAlt /> 124 City Road, Islington, London, EC1V 2NX
            </span>
          </div>
        </div>

        <nav className="footer-nav">
          <div className="footer-col">
            <h3>{t('footer_quick_links')}</h3>
            <ul>
              <li><Link to="/">{t('nav_home')}</Link></li>
              <li><Link to="/about">{t('nav_about')}</Link></li>
              <li><Link to="/find-course">{t('nav_find_course')}</Link></li>
              <li><Link to="/services">{t('nav_services')}</Link></li>
              <li><Link to="/work-with-us">{t('nav_work_with_us')}</Link></li>
              <li><Link to="/contact">{t('nav_contact')}</Link></li>
            </ul>
          </div>

          <div className="footer-col">
            <h3>{t('footer_study_destinations')}</h3>
            {/* City names deliberately kept in English — per the project
                UK-names policy, locations don't translate. Only the heading
                above is localised. */}
            <ul>
              <li><Link to="/find-course?city=london">London</Link></li>
              <li><Link to="/find-course?city=birmingham">Birmingham</Link></li>
              <li><Link to="/find-course?city=manchester">Manchester</Link></li>
              <li><Link to="/find-course?city=leeds">Leeds</Link></li>
              <li><Link to="/find-course?city=nottingham">Nottingham</Link></li>
              <li><Link to="/find-course?city=leicester">Leicester</Link></li>
              <li><Link to="/find-course?city=luton">Luton</Link></li>
            </ul>
          </div>

          <div className="footer-col">
            <h3>{t('footer_discover')}</h3>
            <ul>
              <li><Link to="/#reviews">{t('footer_student_stories')}</Link></li>
              <li><Link to="/events">{t('footer_events')}</Link></li>
              <li><Link to="/blog">{t('footer_blog_news')}</Link></li>
              <li><Link to="/student-guides">{t('footer_student_guides')}</Link></li>
              {/* British Council "Choosing an Agent" guide. Externally-hosted
               * authoritative resource — linking signals AEN endorses the
               * standards. English-only destination, so hreflang="en" hints
               * to AT/Google that the language switches. Footer label uses an
               * i18n key (`footer_agent_guide`) and falls back to English in
               * the 13 non-EN bundles via i18next's fallbackLng. */}
              <li>
                <a
                  href="https://study-uk.shorthandstories.com/study-uk-a-student-guide-to-choosing-an-education-agent/index.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  hrefLang="en"
                  onClick={() => trackEvent('agent_guide_click', { source: 'footer' })}
                >
                  {t('footer_agent_guide')}
                </a>
              </li>
              <li><Link to="/faq">{t('footer_faq')}</Link></li>
              <li><Link to="/funding">{t('footer_funding')}</Link></li>
              <li><a href={STUDENT_LOGIN_HREF} onClick={() => trackEvent('login_click', { type: 'student', source: 'footer' })}>{t('footer_student_login')}</a></li>
              <li><a href={STAFF_LOGIN_HREF}   onClick={() => trackEvent('login_click', { type: 'staff',   source: 'footer' })}>{t('footer_agent_login')}</a></li>
            </ul>
          </div>

          <div className="footer-col">
            <h3>{t('footer_legal')}</h3>
            <ul>
              <li><Link to="/privacy-policy">{t('footer_privacy_policy')}</Link></li>
              <li><Link to="/modern-slavery">{t('footer_modern_slavery')}</Link></li>
              <li><Link to="/legal-disclaimer">{t('footer_legal_disclaimer')}</Link></li>
              <li><Link to="/cookies-policy">{t('footer_cookies_policy')}</Link></li>
            </ul>
          </div>
        </nav>

        {/* ── Partners — sits in col 2 row 2, below the nav ── */}
        <div className="footer-partners">
          <div className="fp-item">
            <img
              src={pearson150}
              srcSet={`${pearson150} 150w, ${pearson400} 400w`}
              sizes="(max-width: 768px) 57px, 88px"
              alt="Pearson"
              className="fp-logo fp-logo-pearson"
              width="400"
              height="181"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="fp-divider" />
          <div className="fp-item"><img src={bcLogo}   alt="British Council" className="fp-logo"                  width="300"  height="86"   loading="lazy" decoding="async" /></div>
          <div className="fp-divider" />
          <div className="fp-item"><img src={ucasLogo} alt="UCAS"            className="fp-logo"                  width="500"  height="192"  loading="lazy" decoding="async" /></div>
        </div>
      </div>

      {/* ── Copyright ── */}
      <div className="footer-bottom">
        <p>{t('footer_copyright', { year: new Date().getFullYear() })}</p>
        <p>{t('footer_registered')}</p>
        {/* Agency credit. "Peak Multimedia" is a brand name and stays in
            English across all 14 locales — only the verb ("Powered by",
            "Desarrollado por", …) is translated via footer_powered_by.
            Opens in a new tab so visitors clicking through don't lose
            their AEN session. rel="noopener" prevents the destination
            from accessing window.opener (security baseline for any
            target="_blank" link). */}
        <p className="footer-credit">
          {t('footer_powered_by')}{' '}
          <a
            href="https://peakmultimedia.co.uk"
            target="_blank"
            rel="noopener noreferrer"
          >
            Peak Multimedia
          </a>
        </p>
      </div>

    </footer>
  );
}
